import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { Meta, Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';

import { DataForImpactPage } from '../../../shared/brands.model';
import { ProjectsService } from '../../../services/projects.service';
import { AuthService } from '../../../services/auth.service';
import { LanguageService } from '../../../services/language.service';
import { RouterUrls } from '../../../shared/router-urls';
import { BrandsService } from '../../../services/brands.service';
import { TagsService } from 'src/app/services/tags.service';
import { MatDialog } from '@angular/material/dialog';
import { ImpactPopupComponent } from 'src/app/shared/impact-popup/impact-popup.component';
import { BrandImpactPopupComponent } from 'src/app/shared/brand-impact-popup/brand-impact-popup.component';
import { HttpErrorResponse } from '@angular/common/http';
import { ModalService } from '../../../shared/services/modal/modal.service';
import { ModalConstants } from '../../../shared/services/modal/modal.constants';

@Component({
  selector: 'app-brand-page',
  templateUrl: './brand-page.component.html',
  styleUrls: ['./brand-page.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class BrandPageComponent implements OnInit {
  impactFilterForm = new UntypedFormGroup({
    filterSelector: new UntypedFormControl('')
  });
  dataForImpactFilter = [];

  brandInfo: DataForImpactPage;
  dataForImpact$: Observable<DataForImpactPage>;
  heightPerso;
  clickEventSubscription: Subscription;
  RouterUrls = RouterUrls;
  paginationLimit = 2;
  brandEndsInS = false;
  isLoading = true;
  mediaList = [];
  screenWidth: number;
  screenHeight: number;
  modalMinHeight: number;
  modalWidth: number;

  constructor(private projectsService: ProjectsService,
              private authService: AuthService,
              private meta: Meta,
              private titleService: Title,
              private tagsService: TagsService,
              private translate: TranslateService,
              private brandsService: BrandsService,
              private router: Router,
              private dialog: MatDialog,
              private activatedRoute: ActivatedRoute,
              private languageService: LanguageService,
              private modal: ModalService) {
  }

  get currentLang() {
    return this.languageService.getCurrentLangWithSlash();
  }

  get currentLanguage() {
    return this.languageService.getUserUsedLanguage();
  }

  get impactFilterControl() {
    return this.impactFilterForm.get('filterSelector');
  }

  get rootShare() {
    return RouterUrls.Brands;
  }

  ngOnInit(): void {
    this.screenWidth = window.innerWidth;
    this.screenHeight = window.innerHeight;
    this.modalMinHeight = this.screenWidth > 767 ? 400 : this.screenHeight;
    this.modalWidth = this.screenWidth > 767 ? 700 : this.screenWidth;

    this.activatedRoute.params.subscribe(() => {
      this.dataForImpact$ = this.activatedRoute.params
        .pipe(
          // Get brand info and save to variable
          switchMap(param => this.brandsService.getBrandInfo(param.slug)),
          tap(value => {
            this.brandInfo = value;
            this.getMedia(value);
            this.brandEndsInS = ['s', 'S'].includes(this.brandInfo.companyInfo.name.slice(-1));

            this.buildDataForFilters();
            this.updateTags();
            this.getImageHeight();
            this.isLoading = false;
          }),
          catchError((error: HttpErrorResponse) => {
            if (error.status === 406) {
              // Status 406 for this call means redirection to a different slug
              const newSlug = error.error.company;
              if (newSlug) {
                this.router.navigate(
                  [this.languageService.getCurrentLangWithSlash(), RouterUrls.Brands, newSlug]);
              } else {
                this.router.navigate(
                  [this.languageService.getCurrentLangWithSlash(), RouterUrls.FourOFour]);
              }
            } else {
              // Redirect to main page if there is no brand with such id
              this.router.navigate([this.languageService.getCurrentLangWithSlash()]);
              return of(error);
            }
          })
        );
      this.clickEventSubscription = this.tagsService.getClickEventBrand().subscribe(() => {
        this.updateTags();
      });
    });
  }

  getImageHeight() {
    const img = new Image();
    img.src = this.brandInfo.companyInfo.bg_image_url;

    img.onload = (event) => {
      const loadedImage = event.currentTarget as HTMLImageElement;
      const height = loadedImage.height;
      this.heightPerso = height + 'px';
    };
  }

  buildDataForFilters() {
    let all: string;
    let employees: string;
    let customers: string;
    let matchingGifts: string;
    switch (this.currentLanguage) {
      case 'fr':
        all = 'Tous';
        employees = 'Collaborateurs';
        customers = 'Clients, fournisseurs et amis';
        matchingGifts = 'Matching gifts';
        break;
      case 'es':
        all = 'Todos';
        employees = 'Colaboradores';
        customers = 'Clientes, proveedores y amigos';
        matchingGifts = 'Matching gifts';
        break;
      default:
        all = 'All';
        employees = 'Employees';
        customers = 'Clients, Suppliers & Friends';
        matchingGifts = 'Matching gifts';
    }
    this.dataForImpactFilter = [
      {radioValue: 'all', shownValue: all},
      {radioValue: 'employees', shownValue: employees},
      {radioValue: 'customers', shownValue: customers},
      {radioValue: 'matching_gifts', shownValue: matchingGifts},
    ];

    this.impactFilterControl.setValue('all');
  }

  updateTags() {
    this.titleService.setTitle(
      this.translate.instant('BRAND_PAGE.meta_title1_brand_page') +
      this.brandInfo.companyInfo.name + this.translate.instant('BRAND_PAGE.meta_title2_brand_page'));
    this.meta.updateTag({name: 'description', content:
      this.translate.instant('BRAND_PAGE.meta_description1_brand_page') +
      this.brandInfo.companyInfo.name + this.translate.instant('BRAND_PAGE.meta_description2_brand_page')});
    this.meta.updateTag({property: 'og:title', content: this.translate.instant('BRAND_PAGE.meta_title1_brand_page') +
      this.brandInfo.companyInfo.name + this.translate.instant('BRAND_PAGE.meta_title2_brand_page')});
    this.meta.updateTag({property: 'og:description', content:
      this.translate.instant('BRAND_PAGE.meta_description1_brand_page') + this.brandInfo.companyInfo.name + ' ' +
      this.translate.instant('BRAND_PAGE.meta_description2_brand_page')});
    this.meta.updateTag({property: 'og:type', content: 'URL'});
    this.meta.updateTag({property: 'og:url', content: window.location.href});
    this.meta.updateTag({property: 'og:image', content: '/assets/image-share/' + this.brandInfo.companyInfo.slug + '_' +
      this.currentLanguage + '.png'});
  }

  getBackgroundImageUrl() {
    return 'background-image: url(' + this.brandInfo.currentProgram.picture_thumbnail + ')';
  }

  url_currentProgram() {
    const type = this.brandInfo.currentProgram.type;
    if (type === 'matching_gift') {
      return 'matching-gift/' + this.brandInfo.currentProgram.slug;
    } else {
      return this.brandInfo.currentProgram.slug;
    }
  }

  get_nb_tons() {
    let nbTons = 0;
    for (const project of this.brandInfo.projects.all) {
      if (project.north_star === 'reforestation') {
        nbTons += project.progress * 12.3 / 1000;
      } else if (project.north_star === 'carbon_offset') {
        nbTons += project.progress;
      }
    }
    return nbTons;
  }

  previousCampaigns() {
    const listPrevious = [];
    for (const customerspledge of Object.keys(this.brandInfo.programs.customers)) {
        if (this.brandInfo.currentProgram.id !== this.brandInfo.programs.customers[customerspledge].id ||
            this.brandInfo.currentProgram.type !== this.brandInfo.programs.customers[customerspledge].type) {
          listPrevious.push(this.brandInfo.programs.customers[customerspledge]);
        }
    }
    for (const matchinggift of Object.keys(this.brandInfo.programs.matchingGift)) {
      if (this.brandInfo.currentProgram.id !== this.brandInfo.programs.matchingGift[matchinggift].id ||
          this.brandInfo.currentProgram.type !== this.brandInfo.programs.matchingGift[matchinggift].type) {
        listPrevious.push(this.brandInfo.programs.matchingGift[matchinggift]);
      }
    }
    return listPrevious;
  }

  showMore() {
    this.paginationLimit += 2;
  }

  openBrandSingleImpactDialog(axis: string[]) {
    const data = { projects: this.brandInfo.projects.all, allImpact: false, correspondingAxis: axis };

    this.modal.openAdaptableModal(data, BrandImpactPopupComponent, this.screenWidth > 767 ?
      ModalConstants.DEFAULT_PANEL_CLASS : ModalConstants.DEFAULT_FULLSCREEN_PANEL_CLASS, this.modalWidth, this.modalMinHeight, this.modalWidth);
  }

  openBrandAllImpactDialog() {
    const data = { projects: this.brandInfo.projects.all, allImpact: true, correspondingAxis: [] };

    this.modal.openAdaptableModal(data, BrandImpactPopupComponent, this.screenWidth > 767 ?
      ModalConstants.DEFAULT_PANEL_CLASS : ModalConstants.DEFAULT_FULLSCREEN_PANEL_CLASS, this.modalWidth, this.modalMinHeight, this.modalWidth);
  }

  openDialog(campaignId: number, campaignType: string) {
    const data = { campaignId, campaignType };

    this.modal.openAdaptableModal(data, ImpactPopupComponent, this.screenWidth > 767 ?
      ModalConstants.DEFAULT_PANEL_CLASS : ModalConstants.DEFAULT_FULLSCREEN_PANEL_CLASS, this.modalWidth, this.modalMinHeight, this.modalWidth);
  }

  getMedia(data: any) {
    for (const item of data.companyInfo.media) {
      if (item) {
        this.mediaList.push({
          url: item,
          description: ''
        });
      }
    }
  }

  navigateToUrl(url: string): void {
    window.open(url, '_self');
  }
}
